import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  MeasurementCardDetails,
  StackNumberingEnumDto,
  ExportFormatEnumDto,
} from '.generated/api';
import { ApiService } from 'services/apiService';
import { notificationActions } from 'actions/notificationActions';
import { organizationActions } from 'actions/organizationActions';

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  failedList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  failedItem: {
    marginBottom: theme.spacing(0.5),
  },
}));

type Props = {
  open: boolean;
  selectedOrganizations: { id: string; name: string }[];
  onClose: () => void;
  onSuccess: () => void;
};

const BulkUpdateMeasurementCardDialog: React.FC<Props> = ({
  open,
  selectedOrganizations,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [measurementCards, setMeasurementCards] = useState<
    MeasurementCardDetails[]
  >([]);
  const [input, setInput] = useState({
    measurementCardId: '',
    stackNumbering: StackNumberingEnumDto.AUTO_GENERATED,
    exportFormat: ExportFormatEnumDto.CSV,
  });

  useEffect(() => {
    if (open) {
      ApiService.getMeasurementCardList()
        .then((response) => {
          const details = response?.data?.details;
          if (details && details.length > 0) {
            setMeasurementCards(details);
            setInput((prev) => ({
              ...prev,
              measurementCardId: details[0].measurementCardId ?? '',
            }));
          }
        })
        .catch((error) => {
          dispatch(notificationActions.enqueueErrorResponse(error));
        });
    }
  }, [open]);

  const handleSelect = (
    inputName: string,
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setInput((prev) => ({ ...prev, [inputName]: e.target.value }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await ApiService.bulkUpdateMeasurementCardInformation(
        selectedOrganizations.map((o) => o.id),
        input.measurementCardId,
        input.stackNumbering,
        input.exportFormat,
      );

      const failedIds = response.data.failedOrganizationIds ?? [];

      if (failedIds.length > 0) {
        const failedNames = failedIds.map(
          (id) => selectedOrganizations.find((o) => o.id === id)?.name ?? id,
        );
        const message =
          t('organizations_page.bulk_update_measurement_card.partial_failure') +
          '\n' +
          failedNames.join(', ');
        dispatch(notificationActions.enqueueError(message));
      }

      if (failedIds.length < selectedOrganizations.length) {
        dispatch(
          notificationActions.enqueueSnackbar(
            t('organizations_page.bulk_update_measurement_card.success'),
            { variant: 'success' },
          ),
        );
        dispatch(organizationActions.getOrganizations());
        onSuccess();
      }
    } catch (error: any) {
      dispatch(notificationActions.enqueueErrorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('organizations_page.bulk_update_measurement_card.title', {
          count: selectedOrganizations.length,
        })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {t('organizations_page.bulk_update_measurement_card.subtitle', {
            count: selectedOrganizations.length,
          })}
        </Typography>

        <FormControl variant="outlined" fullWidth className={classes.field}>
          <InputLabel>
            {t('organization_page.measurement_card.labels.measurement_card')}
          </InputLabel>
          <Select
            value={input.measurementCardId}
            onChange={(e) => handleSelect('measurementCardId', e)}
            label={t(
              'organization_page.measurement_card.labels.measurement_card',
            )}
          >
            {measurementCards.map((card, index) => (
              <MenuItem key={index} value={card.measurementCardId ?? ''}>
                {card.measurementCardName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" fullWidth className={classes.field}>
          <InputLabel>
            {t('organization_page.measurement_card.labels.stack_numbering')}
          </InputLabel>
          <Select
            value={input.stackNumbering}
            onChange={(e) => handleSelect('stackNumbering', e)}
            label={t(
              'organization_page.measurement_card.labels.stack_numbering',
            )}
          >
            <MenuItem value={StackNumberingEnumDto.AUTO_GENERATED}>
              {t(
                'organization_page.measurement_card.stack_numbering_types.autogenerated',
              )}
            </MenuItem>
            <MenuItem value={StackNumberingEnumDto.MANUEL}>
              {t(
                'organization_page.measurement_card.stack_numbering_types.manuel',
              )}
            </MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isLoading || !input.measurementCardId}
        >
          {isLoading ? <CircularProgress size={20} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { BulkUpdateMeasurementCardDialog };
